import type { DocumentType } from '@form-factory/schema';
import { DocumentStatus, Role } from '@form-factory/schema/enums';
import type { PopulateParams } from '@form-factory/shared';
import { useLingui } from '@lingui/react/macro';
import {
  createFileRoute,
  type SearchSchemaInput,
} from '@tanstack/react-router';
import { getQueryKey } from '@trpc/react-query';
import { Button, Separator } from '@utima/ui';
import { FileX2 } from 'lucide-react';
import { useMemo, useState } from 'react';

import { SimpleTooltip } from '@/components';
import type { DataTableRowHandler } from '@/components/dataTable/DataTable';
import { TablePage } from '@/components/page/TablePage';
import { Protected } from '@/components/protected/Protected';
import {
  DocumentsStatusSummary,
  type StatisticItem,
} from '@/features/chats/components/documentsStatusSummary/DocumentsStatusSummary';
import {
  RetryFailedButton,
  AddDocumentsModal,
  DocumentContentModal,
  useDocumentCols,
  useDocumentMutation,
} from '@/features/documents';
import { useTableQuery } from '@/hooks/useTableQuery';
import { trpc, trpcClient } from '@/services/trpc';

const populate: PopulateParams = [
  'documentsCount',
  'parsingErrorDocumentsCount',
  'embeddingErrorDocumentsCount',
  'uploadingDocumentsCount',
  'readyDocumentsCount',
  'parsingDocumentsCount',
  'parsingAsyncDocumentsCount',
  'parsedDocumentsCount',
  'embeddingDocumentsCount',
  'doneDocumentsCount',
  'organization.id',
];

export const Route = createFileRoute('/_baseLayout/chats/$chatId/documents/')({
  component: DocumentsPage,
  loader: async ({ context: { trpcUtils }, params: { chatId } }) =>
    trpcUtils.chats.findOne.ensureData({ id: chatId, populate }),
  validateSearch: (search: { open?: string } & SearchSchemaInput) => {
    if (search?.open) {
      return { open: search.open };
    }
  },
});

export function DocumentsPage() {
  const { chatId } = Route.useParams();
  const [data] = trpc.chats.findOne.useSuspenseQuery({
    id: chatId,
    populate,
  });
  const search = Route.useSearch();
  const { t } = useLingui();
  const [selectedDocument, setSelectedDocument] = useState<DocumentType | null>(
    null,
  );
  const columns = useDocumentCols(chatId);

  const utils = trpc.useUtils();
  const { resetAllEmbeddings } = useDocumentMutation();

  const handleQuery = useTableQuery(trpcClient.documents.findAll.query, {
    populate: ['parsingMetadata', 'shortSummary'],
    where: {
      chats: { id: { $in: [chatId] } },
    },
  });

  const handleRow: DataTableRowHandler<DocumentType> = (_, row) => {
    setSelectedDocument(row.original);
  };

  const statistics = useMemo<StatisticItem[]>(() => {
    return [
      // Error states
      {
        status: DocumentStatus.ParsingError,
        count: Number(data.parsingErrorDocumentsCount || 0),
        isError: true,
      },
      {
        status: DocumentStatus.EmbeddingError,
        count: Number(data.embeddingErrorDocumentsCount || 0),
        isError: true,
      },

      // Processing states
      {
        status: DocumentStatus.Parsing,
        count: Number(data.parsingDocumentsCount || 0),
        isProcessing: true,
      },
      {
        status: DocumentStatus.ParsingAsync,
        count: Number(data.parsingAsyncDocumentsCount || 0),
        isProcessing: true,
      },
      {
        status: DocumentStatus.Parsed,
        count: Number(data.parsedDocumentsCount || 0),
        isProcessing: true,
      },
      {
        status: DocumentStatus.Embedding,
        count: Number(data.embeddingDocumentsCount || 0),
        isProcessing: true,
      },

      // Done state
      {
        status: DocumentStatus.Done,
        count: Number(data.doneDocumentsCount || 0),
        isDone: true,
      },
    ];
  }, [data]);

  return (
    <>
      <TablePage
        title={t`Documents`}
        onRow={handleRow as any}
        actions={
          <>
            <Protected roles={[Role.SuperAdmin]}>
              {/* TODO FIX THIS TS ERROR WITH INFINITE INSTANTIATION */}
              <RetryFailedButton chat={data as any} />
              <SimpleTooltip title={t`Reset all embeddings`}>
                <Button
                  data-cta='admin-documents-reset-all-embeddings-button'
                  variant='danger'
                  outline
                  onClick={async () => {
                    await resetAllEmbeddings.mutateAsync({ chatId });
                    await utils.chats.findOne.invalidate({ id: chatId });
                  }}
                  size='icon-sm'
                >
                  <FileX2 className='size-4' />
                </Button>
              </SimpleTooltip>
              <Separator orientation='vertical' className='h-10' />
            </Protected>
            <AddDocumentsModal
              chatId={chatId}
              defaultOpen={search && 'open' in search}
              defaultUrl={search?.open}
            />
          </>
        }
        breadcrumbs={[
          {
            // TODO FIX THIS TS ERROR WITH INFINITE INSTANTIATION
            label: (data as any).name,
            to: '/chats/$chatId/edit',
            params: { chatId },
          },
          {
            label: t`Documents`,
            to: '/chats/$chatId/documents',
            params: { chatId },
          },
        ]}
        // TODO FIX THIS TS ERROR WITH INFINITE INSTANTIATION
        columns={columns as any}
        queryKey={[...getQueryKey(trpc.documents.findAll), { chatId }]}
        onQueryFn={handleQuery}
      >
        <DocumentsStatusSummary
          title={t`Documents statistics`}
          statistics={statistics}
          // TODO FIX THIS TS ERROR WITH INFINITE INSTANTIATION
          total={Number((data as any).documentsCount || 0)}
        />
      </TablePage>

      <DocumentContentModal
        documentId={selectedDocument?.id}
        onClose={() => setSelectedDocument(null)}
      />
    </>
  );
}
